import { useCallback, useMemo, useState, useEffect, useRef, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

import moment from 'moment-timezone'
import _ from 'lodash'

import {
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { Box, IconButton, Typography, Switch, Stack, Button, Tooltip } from '@mui/material'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'

import { AppContext } from '/providers/app-context'
import { getTemplateData } from '/providers/templates-api'
import { getIssuedPasses } from '/providers/passes-api'
import TemplateSelector from '/components/templates/template-selector'
import StudioTable, { useStudioTable } from '/components/studio-table'

import DataExportDialog from "../data-export-dialog"
import excelExport from './passes-list-to-excel'
import ClickToCopy from '/components/click-to-copy'
import { createSelectableTableCellSx, createSelectableTableRowSx, getSelectableTableRowBackground } from '../table-styles'
import getLeafNodes from '/utils/get-leaf-nodes'

import config from '/providers/config.json'

let passesTableSession = null

const PassesTable = ({ templateId: selectedTemplateId, deletedPassIds = [], onSelectTemplate, onSelectedPassesChange, onSelectedPassChange, onRowDoubleClick, onPassIdClick }) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const { templateId } = useParams()
  const { apiSource, apiKey, client, selectedClientId, passes, setPasses } = useContext(AppContext)
  const sessionKey = `${apiSource}:${selectedClientId ?? client?.client_id ?? 'default'}:${templateId}`
  const [initialSession] = useState(() => passesTableSession?.key === sessionKey ? passesTableSession : null)
  const [restoredPasses] = useState(() => initialSession && passes.length === initialSession.passes.length
    && passes.every(pass => pass.template_id === templateId && pass.mode === initialSession.passMode)
      ? passes
      : initialSession?.passes ?? [])

  const [rowSelection, setRowSelection] = useState(initialSession?.rowSelection ?? {})
  const [selectedPassKey, setSelectedPassKey] = useState(initialSession?.selectedPassKey ?? null)
  const [sorting, setSorting] = useState(initialSession?.sorting ?? [])
  const [columnFilters, setColumnFilters] = useState(initialSession?.columnFilters ?? [])
  const [globalFilter, setGlobalFilter] = useState(initialSession?.globalFilter ?? '')
  const [rowCount, setRowCount] = useState(initialSession?.rowCount ?? 0)
  const [loading, setLoading] = useState(!initialSession)
  const [template, setTemplate] = useState(initialSession?.template)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [passMode, setPassMode] = useState(initialSession?.passMode ?? 'live')
  const [selectAllMatching, setSelectAllMatching] = useState(initialSession?.selectAllMatching ?? false)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const selectionQuery = useMemo(() => buildSelectionQuery(columnFilters, globalFilter), [columnFilters, globalFilter])
  const canSelectAllMatching = Boolean(selectionQuery)

  const totalUpdateCounter = useMemo(() => _.sum(passes.map(d => d.update_counter)), [passes])
  const rowVirtualizerInstanceRef = useRef(null)
  const tableContainerRef = useRef(null)
  const scrollTopRef = useRef(initialSession?.scrollTop ?? 0)
  const passesRef = useRef(restoredPasses)
  const rowCountRef = useRef(initialSession?.rowCount ?? 0)
  const fetchingRef = useRef(false)
  const requestGenerationRef = useRef(0)
  const restoreTemplateRef = useRef(Boolean(initialSession))
  const restoreDataRef = useRef(Boolean(initialSession))
  const latestSessionRef = useRef(null)

  useEffect(() => {
    if (!deletedPassIds.length) return

    const deletedIds = new Set(deletedPassIds)
    const nextPasses = passesRef.current.filter(pass => !deletedIds.has(pass.pass_id))
    const deletedCount = passesRef.current.length - nextPasses.length

    if (!deletedCount) return

    requestGenerationRef.current += 1
    passesRef.current = nextPasses
    rowCountRef.current = Math.max(0, rowCountRef.current - deletedCount)
    fetchingRef.current = false
    setPasses(nextPasses)
    setRowCount(rowCountRef.current)
    setLoading(false)
    setRowSelection({})
    setSelectAllMatching(false)
    setSelectedPassKey(null)
  }, [deletedPassIds, setPasses])

  useEffect(() => {
    let active = true
    requestGenerationRef.current += 1

    if (restoreTemplateRef.current) {
      restoreTemplateRef.current = false
      passesRef.current = restoredPasses
      rowCountRef.current = initialSession.rowCount
      setPasses(restoredPasses)
      setLoading(false)
      return () => { active = false }
    }

    passesTableSession = null
    setTemplate(undefined)
    setSorting([])
    setColumnFilters([])
    setGlobalFilter('')
    setPasses([])
    setRowCount(0)
    setRowSelection({})
    setSelectAllMatching(false)
    setSelectedPassKey(null)

    const loadTemplate = async () => {
      const result = await getTemplateData({ apiSource, apiKey, templateId })
      if (active && !result.error) setTemplate(result.template)
    }

    loadTemplate()
    return () => { active = false }
  }, [apiKey, apiSource, initialSession, restoredPasses, setPasses, templateId])

  const requestedQuery = useMemo(
    () => buildPassServerQuery({ columnFilters, globalFilter, sorting, template }),
    [columnFilters, globalFilter, sorting, template]
  )
  const [serverQuery, setServerQuery] = useState(initialSession?.serverQuery ?? requestedQuery)

  useEffect(() => {
    const timeout = setTimeout(() => setServerQuery(current =>
      JSON.stringify(current) === JSON.stringify(requestedQuery) ? current : requestedQuery
    ), 300)
    return () => clearTimeout(timeout)
  }, [requestedQuery])

  useEffect(() => {
    if (!template) return

    if (restoreDataRef.current) {
      restoreDataRef.current = false
      passesRef.current = restoredPasses
      rowCountRef.current = initialSession.rowCount
      fetchingRef.current = false
      setPasses(restoredPasses)
      setRowCount(initialSession.rowCount)
      setLoading(false)
      requestAnimationFrame(() => {
        if (tableContainerRef.current) tableContainerRef.current.scrollTop = initialSession.scrollTop ?? 0
      })
      return
    }

    const generation = ++requestGenerationRef.current
    passesRef.current = []
    rowCountRef.current = 0
    fetchingRef.current = true
    setLoading(true)
    setPasses([])
    setRowCount(0)
    setRowSelection({})
    setSelectAllMatching(false)
    setSelectedPassKey(null)
    rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)

    const loadInitialPage = async () => {
      const result = await getIssuedPasses({
        apiSource,
        apiKey,
        templateId,
        mode: passMode,
        startRow: 0,
        maxRows: config.maxPassesPerRequest,
        query: serverQuery,
      })

      if (generation !== requestGenerationRef.current) return
      const nextPasses = result?.passes ?? []
      const matchingCount = Number(result?.rowCount ?? 0)
      passesRef.current = nextPasses
      rowCountRef.current = matchingCount
      setPasses(nextPasses)
      setRowCount(matchingCount)
      setLoading(false)
      fetchingRef.current = false
    }

    loadInitialPage()
  }, [apiKey, apiSource, initialSession, passMode, refreshCounter, restoredPasses, serverQuery, setPasses, template, templateId])

  const loadMore = useCallback(async () => {
    if (fetchingRef.current || passesRef.current.length >= rowCountRef.current) return

    const generation = requestGenerationRef.current
    fetchingRef.current = true
    setLoading(true)

    try {
      const result = await getIssuedPasses({
        apiSource,
        apiKey,
        templateId,
        mode: passMode,
        startRow: passesRef.current.length,
        maxRows: config.maxPassesPerRequest,
        query: serverQuery,
      })

      if (generation !== requestGenerationRef.current) return
      if (result?.error) return

      const nextPasses = [...passesRef.current, ...(result?.passes ?? [])]
      const matchingCount = Number(result?.rowCount ?? rowCountRef.current)
      passesRef.current = nextPasses
      rowCountRef.current = matchingCount
      setPasses(nextPasses)
      setRowCount(matchingCount)
    } finally {
      if (generation === requestGenerationRef.current) {
        setLoading(false)
        fetchingRef.current = false
      }
    }
  }, [apiKey, apiSource, passMode, serverQuery, setPasses, templateId])

  const fetchMoreOnBottomReached = useCallback(event => {
    const container = event.currentTarget
    scrollTopRef.current = container.scrollTop
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 500) loadMore()
  }, [loadMore])

  const updateRowSelection = updater => {
    setSelectAllMatching(false)
    setRowSelection(updater)
  }

  const clearSelections = () => {
    setRowSelection({})
    setSelectAllMatching(false)
    setSelectedPassKey(null)
  }

  const clearRowsForQueryChange = () => {
    requestGenerationRef.current += 1
    passesRef.current = []
    rowCountRef.current = 0
    fetchingRef.current = false
    setPasses([])
    setRowCount(0)
  }

  const updateColumnFilters = updater => {
    clearSelections()
    clearRowsForQueryChange()
    setColumnFilters(updater)
  }

  const updateGlobalFilter = updater => {
    clearSelections()
    clearRowsForQueryChange()
    setGlobalFilter(updater)
  }

  const updateSorting = updater => {
    clearSelections()
    clearRowsForQueryChange()
    setSorting(updater)
  }

  const updateSelectAllMatching = event => {
    const checked = event.target.checked
    // A query-wide selection is only available when the table is filtered.
    // Without a filter, select the currently loaded rows explicitly.
    setSelectAllMatching(checked && canSelectAllMatching)
    setRowSelection(checked ? Object.fromEntries(passes.map(pass => [pass.pass_id, true])) : {})
  }

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(passId => rowSelection[passId])
    const loadedPasses = passes.filter(pass => selectedIds.includes(pass.pass_id))
    onSelectedPassesChange?.(selectAllMatching && selectionQuery
      ? { type: 'allMatching', count: rowCount, mode: passMode, query: selectionQuery, loadedPasses }
      : { type: 'loaded', count: loadedPasses.length, mode: passMode, passes: loadedPasses }
    )
  }, [rowSelection, passes, onSelectedPassesChange, passMode, rowCount, selectAllMatching, selectionQuery])

  useEffect(() => {
    const selectedPass = selectedPassKey?.templateId === templateId
      ? passes.find(pass => pass.pass_id === selectedPassKey.passId) ?? null
      : null
    onSelectedPassChange?.(selectedPass)
  }, [selectedPassKey, templateId, passes, onSelectedPassChange])

  useEffect(() => {
    if (!selectAllMatching) return
    setRowSelection(Object.fromEntries(passes.map(pass => [pass.pass_id, true])))
  }, [passes, selectAllMatching])

  useEffect(() => {
    latestSessionRef.current = template ? {
      key: sessionKey,
      template,
      passes,
      rowCount,
      rowSelection,
      selectedPassKey,
      sorting,
      columnFilters,
      globalFilter,
      serverQuery,
      passMode,
      selectAllMatching,
    } : null

    if (latestSessionRef.current) {
      passesTableSession = {
        ...latestSessionRef.current,
        scrollTop: scrollTopRef.current,
      }
    }
  }, [columnFilters, globalFilter, passMode, passes, rowCount, rowSelection, selectAllMatching, selectedPassKey, serverQuery, sessionKey, sorting, template])

  useEffect(() => () => {
    if (latestSessionRef.current) {
      passesTableSession = {
        ...latestSessionRef.current,
        scrollTop: scrollTopRef.current,
      }
    }
  }, [])

  const templateVariableColumns = useMemo(() => {
    const templateLeafNodes = new Set(getLeafNodes(template?.variables ?? {}))
    const isManagedPass = Number(template?.managed_pass_template) === 1
    const catalogItems = (template?.variable_catalog ?? [])
      .filter(item => Number(item.encrypted) === 0 && templateLeafNodes.has(item.var_name))
      .filter(item => !(isManagedPass && item.var_name.startsWith('barcode.')))
      .filter(item => !isHiddenTableVariable(item.var_name))

    return catalogItems.map(item => buildManagedColumn(item, template?.variables ?? {}))
  }, [template])

  const columns = useMemo(() => {

    if (!template) return []

    let cols = [
      {
        accessorKey: 'pass_id',
        header: 'Pass ID',
        filterVariant: 'text',
        size: 300,
        Footer: () => "Totals",
        Cell: ({ cell, row }) => <div className='link' onClick={() => onPassIdClick ? onPassIdClick(row.original) : navigate(row.id + "/details")}><OpenInNewIcon className='mr-5 grey' />{cell.getValue()}</div>
      },
      {
        id: 'short_install_url',
        accessorFn: originalRow => originalRow.public_id ? `https://api.smartix.uk/i/${originalRow.public_id}` : '',
        header: 'Install URL',
        enableColumnFilter: false,
        enableSorting: false,
        size: 400,
        Cell: ({ cell }) => {
          const url = cell.getValue()
          return url ? <ClickToCopy source={url} successMessage='Short install URL copied'>{url}</ClickToCopy> : '-'
        }
      },
      {
        id: 'created_date',
        accessorFn: (originalRow) => moment.utc(originalRow.created_date).tz('Europe/London'),
        header: 'Created',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        size: 200,
        Cell: ({ cell }) => {
          const date = cell.getValue()
          if (date.isValid()) return date.format("DD MMM YYYY HH:mm")
          return "-"
        }
      },
      {
        id: 'active',
        accessorFn: (originalRow) => !originalRow.expiry_date || moment.utc(originalRow.expiry_date).isAfter(moment.utc()),
        header: 'Status',
        filterVariant: 'select',
        filterFn: 'equals',
        filterSelectOptions: [
          { label: 'Current', value: "true" },
          { label: 'Expired', value: "false" },
        ],
        size: 200,
        Cell: ({ cell }) => cell.getValue() ? <span className='green'>current</span> : <span className='red'>expired</span>
      },
      {
        id: 'expiry_date',
        accessorFn: (originalRow) => moment.utc(originalRow.expiry_date).tz('Europe/London'),
        header: 'Expiry Date',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        size: 200,
        Cell: ({ cell }) => {
          const date = cell.getValue()
          if (date.isValid()) return date.format("DD MMM YYYY HH:mm")
          return "-"
        }
      },
      {
        accessorKey: 'install_status',
        header: 'Install Status',
        filterVariant: 'select',
        filterFn: 'equals',
        filterSelectOptions: [
          { label: 'In Use', value: "in_use" },
          { label: 'Dormant', value: "dormant" },
          { label: 'Never Used', value: "never_used" },
        ],
        size: 160,
        Cell: ({ cell }) => installStatusLabel(cell.getValue())
      },
      {
        accessorKey: 'update_counter',
        header: 'Updates, syncs and notifications today',
        filterVariant: 'range',
        filterFn: 'between',
        size: 200,
        Cell: ({ cell }) => numberFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableFooterCellProps: {
          align: 'right',
        },
        Footer: () => totalUpdateCounter
      },
      {
        accessorFn: originalRow => Number(originalRow.free_engagements_remaining ?? 0),
        id: 'free_engagements_remaining',
        header: 'Free engagements left',
        filterVariant: 'range',
        filterFn: 'between',
        size: 180,
        Cell: ({ cell }) => numberFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
      },
    ]

    return [...cols, ...templateVariableColumns]
  }, [template, totalUpdateCounter, templateVariableColumns])

  const table = useStudioTable({
    tableKey: 'passes',
    columns,
    data: passes,
    state: {
      isLoading: false,
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
      showProgressBars: loading,
    },
    initialState: { showColumnFilters: false },
    localization: {
      noRecordsToDisplay: loading ? 'Loading…' : 'No records to display',
      noResultsFound: loading ? 'Loading…' : 'No results found'
    },
    onRowSelectionChange: updateRowSelection,
    onColumnFiltersChange: updateColumnFilters,
    onGlobalFilterChange: updateGlobalFilter,
    manualFiltering: true,
    manualSorting: true,
    enablePagination: false,
    enableBottomToolbar: true,
    enableRowSelection: Boolean(template) && passes.length > 0,
    enableMultiRowSelection: true,
    enableBatchRowSelection: true,
    muiSelectAllCheckboxProps: {
      checked: (selectAllMatching || (!canSelectAllMatching && passes.length > 0 && Object.keys(rowSelection).length === passes.length)) && rowCount > 0,
      indeterminate: !selectAllMatching && Object.keys(rowSelection).some(passId => rowSelection[passId]),
      onChange: updateSelectAllMatching,
      inputProps: { 'aria-label': canSelectAllMatching ? 'Select all matching passes' : 'Select loaded passes' },
    },
    muiTableBodyRowProps: ({ row }) => {
      const isCurrentPass = selectedPassKey?.templateId === templateId && row.original.pass_id === selectedPassKey.passId
      const rowBackground = getSelectableTableRowBackground(theme, { current: isCurrentPass, selected: row.getIsSelected() })

      return {
        'data-pass-id': row.original.pass_id,
        'data-selection-state': isCurrentPass ? 'current' : row.getIsSelected() ? 'checked' : 'none',
        onClick: event => {
          if (event.target.closest('input[type="checkbox"], [role="checkbox"], .MuiCheckbox-root')) return
          setSelectedPassKey(current => current?.templateId === templateId && current.passId === row.original.pass_id
            ? null
            : { templateId, passId: row.original.pass_id }
          )
        },
        onDoubleClick: () => {
          setSelectedPassKey({ templateId, passId: row.original.pass_id })
          onRowDoubleClick?.(row.original)
        },
        style: { backgroundColor: rowBackground },
        sx: createSelectableTableRowSx(theme, { current: isCurrentPass, selected: row.getIsSelected() })
      }
    },
    muiTableBodyCellProps: ({ row }) => {
      const isCurrentPass = selectedPassKey?.templateId === templateId && row.original.pass_id === selectedPassKey.passId
      const cellBackground = getSelectableTableRowBackground(theme, { current: isCurrentPass, selected: row.getIsSelected() })

      return {
        style: { backgroundColor: cellBackground },
        sx: createSelectableTableCellSx(theme, { current: isCurrentPass, selected: row.getIsSelected() })
      }
    },
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowVirtualization: passes.length > 10,
    rowVirtualizerInstanceRef,
    muiTableContainerProps: {
      ref: tableContainerRef,
      onScroll: fetchMoreOnBottomReached,
    },
    muiTableBodyProps: passes.length === 1 ? {
      sx: {
        '& > tr': {
          height: '56px !important',
          maxHeight: '56px !important',
          '& > td': {
            height: '56px !important',
            maxHeight: '56px !important',
            verticalAlign: 'top !important',
          },
        },
      },
    } : passes.length <= 10 ? {
      sx: {
        // Add empty body space without changing the height of actual rows.
        '&::after': {
          content: '""',
          display: 'table-row',
          height: 'calc(100vh - 520px)',
        },
      },
    } : undefined,
    positionToolbarAlertBanner: "none",
    rowCount: rowCount,
    onSortingChange: updateSorting,
    getRowId: (originalRow) => originalRow.pass_id,
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row gap-10'>
        {onSelectTemplate && <TemplateSelector templateId={selectedTemplateId} onChange={onSelectTemplate} />}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>Test Passes</Typography>
          <Switch checked={passMode === "live"} onChange={e => { clearSelections(); setPassMode(e.target.checked ? "live" : "test") }} />
          <Typography>Live Passes</Typography>
        </Stack>

        <Button onClick={() => { clearSelections(); setRefreshCounter(current => current + 1) }}><SyncOutlinedIcon className='mr-2' />Refresh data</Button>
      </div>
    ),
    renderBottomToolbarCustomActions: () => (
      <Typography variant='body2' sx={{ px: 2, py: 1 }}>
        {rowCount.toLocaleString()} matching / {passes.length.toLocaleString()} loaded
        {selectAllMatching ? ` / all ${rowCount.toLocaleString()} selected` : ''}
      </Typography>
    ),
  })

  return (
    <>
      <StudioTable table={table} />
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${template?.template_id}-passes`} data={{ [passMode]: passes }} excelExport={excelExport} template={template} table={table} />
    </>
  )
}

export default PassesTable

const numberFormat = value => <span className={value === 0 ? "grey" : ""}>{value?.toLocaleString()}</span>
const moneyFormat = value => <span className={value === 0 ? "grey" : ""}>{value ? "£" + value.toFixed(2) : "-"}</span>
const formatMomentCell = value => value?.isValid?.() ? value.format("DD MMM YYYY HH:mm") : "-"
const formatDateCell = value => value?.isValid?.() ? value.format("DD MMM YYYY") : "-"
const installStatusLabel = value => {
  switch (value) {
    case "in_use": return <span className='green'>in use</span>
    case "dormant": return <span className='orange'>dormant</span>
    default: return <span className='grey'>never used</span>
  }
}

const prettifyVariableName = varName => varName
  .split(".")
  .map(part => part.replace(/_/g, " "))
  .join(" / ")

const isHiddenTableVariable = varName =>
  varName === 'barcode.format' ||
  varName === 'barcode.test' ||
  varName.startsWith('colours.') ||
  varName.startsWith('images.') ||
  varName.startsWith('pass.')

const getManagedVariableValue = (row, varName, templateVariables) => {
  const passValue = _.get(row.variables, varName)
  if (passValue !== undefined && passValue !== null) return passValue
  return _.get(templateVariables, varName)
}

const buildManagedColumn = (item, templateVariables) => {
  const header = prettifyVariableName(item.var_name)
  const varType = item.var_type ?? "text"

  if (["integer", "number", "money"].includes(varType)) {
    return {
      id: item.var_name,
      accessorFn: originalRow => getManagedVariableValue(originalRow, item.var_name, templateVariables),
      header,
      size: 200,
      filterVariant: 'range',
      filterFn: 'between',
      Cell: ({ cell }) => varType === "money" ? moneyFormat(cell.getValue()) : numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      }
    }
  }

  if (varType === "boolean") {
    return {
      id: item.var_name,
      accessorFn: originalRow => getManagedVariableValue(originalRow, item.var_name, templateVariables),
      header,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'True', value: "true" },
        { label: 'False', value: "false" },
      ],
      size: 160,
      Cell: ({ cell }) => cell.getValue() ? <span className='green'>true</span> : <span className='red'>false</span>
    }
  }

  if (varType === "date") {
    return {
      id: item.var_name,
      accessorFn: originalRow => {
        const value = getManagedVariableValue(originalRow, item.var_name, templateVariables)
        return value ? moment(value, "YYYYMMDD") : null
      },
      header,
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 180,
      Cell: ({ cell }) => formatDateCell(cell.getValue())
    }
  }

  if (varType === "datetime") {
    return {
      id: item.var_name,
      accessorFn: originalRow => {
        const value = getManagedVariableValue(originalRow, item.var_name, templateVariables)
        return value ? moment(value, "YYYYMMDDHHmmss") : null
      },
      header,
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 200,
      Cell: ({ cell }) => formatMomentCell(cell.getValue())
    }
  }

  return {
    id: item.var_name,
    accessorFn: originalRow => getManagedVariableValue(originalRow, item.var_name, templateVariables),
    header,
    filterVariant: 'text',
    size: 220
  }
}

const CORE_SERVER_COLUMNS = {
  pass_id: { type: 'text' },
  created_date: { type: 'utc_datetime' },
  active: { type: 'boolean', filterMode: 'select' },
  expiry_date: { type: 'utc_datetime' },
  install_status: { type: 'text', filterMode: 'select' },
  update_counter: { type: 'number' },
}

const buildPassServerQuery = ({ columnFilters, globalFilter, sorting, template }) => {
  const catalogByName = new Map((template?.variable_catalog ?? []).map(item => [item.var_name, item]))

  const columnDetails = id => {
    if (CORE_SERVER_COLUMNS[id]) return CORE_SERVER_COLUMNS[id]
    const item = catalogByName.get(id)
    return {
      type: item?.var_type ?? 'text',
      filterMode: ['boolean', 'choice'].includes(item?.var_type) ? 'select' : undefined,
      defaultValue: _.get(template?.variables, id),
    }
  }

  return {
    scope: 'matching',
    filters: columnFilters.map(filter => {
      const details = columnDetails(filter.id)
      return {
        id: filter.id,
        value: serialiseServerFilterValue(filter.value, details.type),
        ...details,
      }
    }),
    sorting: sorting.map(sort => ({ ...sort, ...columnDetails(sort.id) })),
    globalFilter: globalFilter ?? '',
  }
}

const serialiseServerFilterValue = (value, type) => {
  if (Array.isArray(value)) {
    return value.map((item, index) => serialiseServerScalar(item, type, index === 1))
  }
  return serialiseServerScalar(value, type, false)
}

const serialiseServerScalar = (value, type, upperBound) => {
  if (value === undefined || value === null || value === '') return value
  if (!['date', 'datetime', 'utc_datetime'].includes(type)) return value

  const date = moment(value)
  if (!date.isValid()) return value
  if (upperBound) date.endOf('day')

  if (type === 'date') return date.format('YYYYMMDD')
  if (type === 'datetime') return date.format('YYYYMMDDHHmmss')
  return date.utc().format('YYYY-MM-DD HH:mm:ss')
}

const NON_VARIABLE_COLUMNS = new Set(['pass_id', 'created_date', 'active', 'expiry_date', 'install_status', 'update_counter'])

const buildSelectionQuery = (columnFilters, globalFilter) => {
  // The bulk API intentionally supports only exact and range variable filters.
  // A text/global search is not converted because its table "contains" semantics
  // would not be equivalent to the API's exact `matches` operation.
  if (globalFilter?.trim()) return null

  const conditions = []
  for (const filter of columnFilters) {
    if (NON_VARIABLE_COLUMNS.has(filter.id)) return null
    const value = filter.value
    if (Array.isArray(value)) {
      const [minimum, maximum] = value
      if (minimum === undefined || minimum === null || minimum === '') {
        if (maximum === undefined || maximum === null || maximum === '') continue
        conditions.push({ id: filter.id, equalOrLessThan: maximum })
      } else if (maximum === undefined || maximum === null || maximum === '') {
        conditions.push({ id: filter.id, equalOrGreaterThan: minimum })
      } else {
        conditions.push({ id: filter.id, between: [minimum, maximum] })
      }
      continue
    }

    if (value === undefined || value === null || value === '') continue
    if (filter.filterMode === 'select' || ['boolean', 'choice'].includes(filter.type)) {
      conditions.push({ id: filter.id, matches: value })
      continue
    }

    return null
  }

  return conditions.length ? [conditions] : null
}
